import { useEffect, useMemo, useRef, useState } from 'react';

import { Button, Tooltip } from '@poseidon/components/Input2';
import { General } from '@poseidon/components/StrokeIcons';

import type { SequenceStep } from '~/components/Sequence/sequenceSteps';
import { SequenceTextLegend } from '~/components/Sequence/SequenceTextLegend';
import { serialize } from '~/components/Sequence/sequenceText';

/** How long the Copy button holds its "Copied" / "Couldn't copy" confirmation. */
const COPY_FEEDBACK_MS = 1500;

/**
 * The sequence's canonical text form (`sequenceText.serialize`), pinned to the bottom-left of the builder
 * canvas and recomputed as you edit — e.g. `C -> W3 -> M1 -> ?AUM{ 0..1000000: E1, *: F }`.
 *
 * Only the `text` half is shown. Message / email bodies ride in the sidecar `config` keyed by the labels on
 * the line (that separation is the whole point of the format — see the module doc in `sequenceText.ts`), so
 * this strip is a read-out of the shape, never a lossless dump and never something to paste back.
 *
 * `serialize` encodes the CANONICAL graph, and mid-authoring the graph often isn't canonical yet: a
 * freshly-inserted parallel group is `arms: [[], []]` (SequenceGraphEditor `insertContainerAt`), which
 * `serialize` cannot encode. That is a normal state to be in for as long as it takes to fill the arms, so
 * the strip says so and stays put — it must never blank itself out mid-edit, and must never take the
 * editor down with it.
 */
export function SequenceTextStrip({ steps }: { steps: SequenceStep[] }) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // One pending confirmation at a time, and none left scheduled on an unmounted tree.
  useEffect(() => () => {
    if (feedbackTimer.current != null) clearTimeout(feedbackTimer.current);
  }, []);

  const text = useMemo(() => {
    if (steps.length === 0) return null;
    try {
      return serialize(steps).text;
    } catch {
      return null; // Not encodable yet — an arm or branch is still empty.
    }
  }, [steps]);

  if (steps.length === 0) return null;

  const copy = async () => {
    if (text == null) return;
    let ok = true;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      ok = false; // Clipboard blocked (permissions / insecure context) — say so rather than look inert.
    }
    setCopyState(ok ? 'copied' : 'failed');
    if (feedbackTimer.current != null) clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => setCopyState('idle'), COPY_FEEDBACK_MS);
  };

  return (
    <div
      data-testid="sequence-text-strip"
      // Left-anchored and width-capped so it can never run under the zoom controls / "Other states" pile
      // in the opposite corner.
      className="absolute bottom-3 left-3 z-10 flex max-w-[calc(100%-17rem)] items-center gap-2 rounded-lg border border-gray-200 bg-white/95 py-1 pl-2.5 pr-1 shadow-sm dark:border-gray-700 dark:bg-slate-900/95">
      {/* The strip lives in the canvas's bottom-LEFT corner, so the legend has to grow up-and-right — the
          default centered span runs off the left edge of the viewport. `shrink-0` and `tabIndex` belong on
          the Tooltip: it renders the element that is actually the flex child and the focus target (the
          repo's Tooltip API note), and its focus handlers are the only way to reach the legend by keyboard. */}
      <Tooltip
        data-testid="sequence-text-legend"
        className="shrink-0"
        tabIndex={0}
        tooltip={<SequenceTextLegend />}
        tooltipMaxWidth={420}
        tooltipPosition="top span-right">
        <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Text</span>
      </Tooltip>

      {/* tabIndex so a line longer than the strip's width cap can be scrolled without a trackpad. */}
      {text != null && (
        <code
          tabIndex={0}
          className="min-w-0 flex-1 select-text overflow-x-auto whitespace-nowrap font-mono text-xs text-gray-700 dark:text-gray-200">
          {text}
        </code>
      )}
      {text == null && (
        <span className="min-w-0 flex-1 whitespace-nowrap text-xs italic text-gray-400 dark:text-gray-500">
          Fill every empty arm to see the line
        </span>
      )}

      <Button
        size="2xs"
        tone="outline"
        type="button"
        disabled={text == null}
        color={copyState === 'failed' ? 'red' : 'gray'}
        icon={copyState === 'copied' ? <General.Check /> : <General.Copy01 />}
        tooltip="Copy this line — the shape only; the written content is not in it"
        onClick={copy}
        className="shrink-0">
        {copyState === 'copied' ? 'Copied' : copyState === 'failed' ? "Couldn't copy" : 'Copy'}
      </Button>
    </div>
  );
}
